import { useReducer, useState, useEffect, useMemo } from 'react'
import { Button } from 'Common/Supplyvue-ui'
import {
  Box,
  MenuItem as MuiMenuItem,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { SettingsPage } from 'Features/Projects/Scenarios/EditSettings/SettingsPage'
import { FormRow } from 'Common/Components/FormRow'
import {
  Config,
  FormActions,
  FormState,
  SalesRatePeriodOptions,
} from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/SalesRate.types'
import {
  useCreateScenarioConfig,
  useSalesRate,
  useUpdateSalesRate,
} from '../../Scenarios.data'
import { useParams } from 'react-router-dom'
import { FORECAST, HISTORICAL } from '../../CreateScenario.page'
import { DatePickerMUI } from '../../Calender'
import dayjs, { Dayjs } from 'dayjs'
import { Loader } from 'Common/Loader/Loader'
import { toast } from 'react-hot-toast'

// Until the scenario's saved window loads.
export const INITIAL_FORM_STATE: FormState = {
  salesRatePeriodType: HISTORICAL,
  periodStartWeek: dayjs(),
  numberOfWeeks: 1,
}

export const formReducer = <T extends keyof FormState>(
  state: FormState,
  action: FormActions<T>
): FormState => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return { ...state, [action.field]: action.value }
    case 'RESET_FORM':
      return INITIAL_FORM_STATE
    case 'SET_FORM':
      return action.value
    default:
      return state
  }
}

const useFormState = () => {
  return useReducer(formReducer, INITIAL_FORM_STATE)
}

type MenuItem = {
  value: string | number
  label: string
}

const salesRateTypeDropDownValues: MenuItem[] = [
  {
    value: HISTORICAL,
    label: 'Historical',
  },
  {
    value: FORECAST,
    label: 'Forecast',
  },
]

const config: Config = {
  maxDate: dayjs(),
  minDate: dayjs(),
  numWeeks: 0,
}

export const SalesRateView = () => {
  const [state, dispatch] = useFormState()
  const { projectId, scenarioId } = useParams<{
    projectId: string
    scenarioId: string
  }>()
  const { data, isLoading } = useCreateScenarioConfig(
    projectId as string,
    state.salesRatePeriodType
  )

  const { data: salesRateData } = useSalesRate(projectId, scenarioId)

  const [configData, setConfigData] = useState(config)

  // The window the rates were last averaged over (the API saves it with
  // each Apply). Cancel goes back to it.
  const savedForm = useMemo<FormState | null>(
    () =>
      salesRateData
        ? {
            salesRatePeriodType:
              salesRateData.salesRatePeriodType ?? HISTORICAL,
            periodStartWeek: dayjs(salesRateData.periodStartWeek),
            numberOfWeeks: Number(salesRateData.numberOfWeeks) || 1,
          }
        : null,
    [salesRateData]
  )

  useEffect(() => {
    if (savedForm) dispatch({ type: 'SET_FORM', value: savedForm })
  }, [savedForm])

  // The dates the chosen demand covers. The start week only moves when it
  // falls outside them (switching between historical and forecast); it used
  // to be reset to the first week whenever these loaded, which could undo
  // the saved window depending on which request finished last.
  useEffect(() => {
    if (data) {
      const maxDate = dayjs(data.maxDate)
      const minDate = dayjs(data.minDate)
      setConfigData({
        maxDate: maxDate,
        minDate: minDate,
        numWeeks: data.numWeeks,
      })
      if (
        state.periodStartWeek.isBefore(minDate, 'day') ||
        state.periodStartWeek.isAfter(maxDate, 'day')
      ) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'periodStartWeek',
          value: minDate,
        })
      }
    }
    // Only when the date range changes, not on every start-week edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const weekSelectionDropDown = useMemo(() => {
    const weeks = []
    const startWeek = state.periodStartWeek
    const endweek = configData.maxDate
    const diff = endweek.diff(startWeek, 'week')
    for (let i = 1; i <= diff; i++) {
      weeks.push({
        value: i,
        label: `${i} weeks`,
      })
    }
    return weeks
  }, [state.periodStartWeek, configData.maxDate])

  const handleChange = (
    e: SelectChangeEvent<{ name?: string; value: unknown }>
  ) => {
    dispatch({
      type: 'UPDATE_FIELD',
      field: e.target.name as keyof FormState,
      value: e.target.value as number | SalesRatePeriodOptions | Dayjs,
    })
  }

  const handleCancel = () => {
    dispatch(
      savedForm
        ? { type: 'SET_FORM', value: savedForm }
        : { type: 'RESET_FORM' }
    )
  }
  const updateSalesRateSuccess = () => {
    toast.success('Sales rate updated successfully')
  }
  const updateSalesRateFailure = (message?: string) => {
    toast.error(message || 'Failed to update sales rate')
  }
  const { mutate: updateSalesRate, isPending: isUpdating } = useUpdateSalesRate(
    updateSalesRateSuccess,
    updateSalesRateFailure
  )

  const handleUpdateSalesRate = () => {
    const updateParams = {
      projectId: projectId as string,
      scenarioId: scenarioId as string,
      data: {
        ...state,
      },
    }
    updateSalesRate(updateParams)
  }
  const busy = isLoading || isUpdating

  return (
    <SettingsPage
      title="Sales rate"
      actions={
        <>
          <Button variant="outlined" onClick={handleCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateSalesRate}
            disabled={busy}
          >
            Apply
          </Button>
        </>
      }
    >
      {busy ? (
        <Loader />
      ) : (
        <Box
          sx={{
            maxWidth: 560,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            p: '16px',
            background: 'var(--surface-0)',
            border: '1px solid var(--line-200)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-panel)',
          }}
        >
          <FormRow
            label="Sales rate period type"
            htmlFor="sales-rate-period-type"
          >
            <TextField
              select
              size="small"
              fullWidth
              id="sales-rate-period-type"
              name="salesRatePeriodType"
              value={state.salesRatePeriodType}
              onChange={(event) =>
                handleChange(
                  event as unknown as SelectChangeEvent<{
                    name?: string
                    value: unknown
                  }>
                )
              }
            >
              {salesRateTypeDropDownValues.map((item) => (
                <MuiMenuItem key={item.value} value={item.value}>
                  {item.label}
                </MuiMenuItem>
              ))}
            </TextField>
          </FormRow>
          <FormRow label="Period start week" htmlFor="period-start-week">
            <DatePickerMUI
              sx={{ width: '100%' }}
              slotProps={{
                textField: {
                  id: 'period-start-week',
                  size: 'small',
                  fullWidth: true,
                },
              }}
              value={state.periodStartWeek}
              onChange={(value: Dayjs) =>
                dispatch({
                  type: 'UPDATE_FIELD',
                  field: 'periodStartWeek',
                  value: value,
                })
              }
              format="DD-MM-YYYY"
              minDate={configData.minDate}
              maxDate={configData.maxDate}
              shouldDisableDate={(date: Dayjs) => date.day() !== 1}
            />
          </FormRow>
          <FormRow label="Number of weeks" htmlFor="number-of-weeks">
            <TextField
              select
              size="small"
              fullWidth
              id="number-of-weeks"
              name="number-of-weeks"
              value={state.numberOfWeeks}
              onChange={(event) =>
                dispatch({
                  type: 'UPDATE_FIELD',
                  field: 'numberOfWeeks',
                  value: Number(event.target.value),
                })
              }
            >
              {weekSelectionDropDown.map((item) => (
                <MuiMenuItem key={item.value} value={item.value}>
                  {item.label}
                </MuiMenuItem>
              ))}
            </TextField>
          </FormRow>
        </Box>
      )}
    </SettingsPage>
  )
}
